import {extractIndoorMapLink} from "../util/util";
import React, {useEffect, useMemo} from "react";
import EmbeddedEliMap from "./map/EliMaps";

const GOOGLE_MARKER_ICON = {
    url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    width: 32,
    height: 32
};

const Maps = ({
    call,
    autoRefreshMap = true,
    showAllMapMarkers = false,
    showGpsMarkers = false,
    debugMode = false,
    selectedLogIndex = null,
    useAlsMarker = true,
    onMarkerWarningChange = () => {}
}) => {
    const history = useMemo(() => call?.eml || [], [call]);
    const markerSourceId = call?.sourceIdentifier1 || "";
    const initPayload = useMemo(() => {
        for (const historyItem of history) {
            const payload = historyItem?.payload?.[0] || null;
            if (payload?.rankedLocations?.[0]?.tenantPublicId) {
                return payload;
            }
        }

        return null;
    }, [history]);
    const selectedHistoryItem = useMemo(() => {
        if (!history.length) {
            return null;
        }

        if (autoRefreshMap) {
            return history[0];
        }

        return history[selectedLogIndex] || history[0];
    }, [autoRefreshMap, history, selectedLogIndex]);
    const currentHistoryIndex = useMemo(() => {
        if (!history.length) {
            return null;
        }

        if (autoRefreshMap) {
            return 0;
        }

        return history[selectedLogIndex] ? selectedLogIndex : 0;
    }, [autoRefreshMap, history, selectedLogIndex]);
    const currentPayload = selectedHistoryItem?.payload?.[0] || null;
    const initMapData = useMemo(() => extractInitMapDataFromPayload(initPayload), [initPayload]);
    const currentMapData = useMemo(() => extractMarkerDataFromPayload(
        currentPayload,
        useAlsMarker,
        buildMarkerLabel(markerSourceId, currentHistoryIndex),
        showGpsMarkers
    ), [currentHistoryIndex, currentPayload, markerSourceId, showGpsMarkers, useAlsMarker]);
    const currentGpsMapData = useMemo(() => extractDeviceGpsMarkerDataFromPayload(
        currentPayload,
        buildMarkerLabel(markerSourceId, currentHistoryIndex),
        `gps-marker-${currentHistoryIndex ?? 0}`
    ), [currentHistoryIndex, currentPayload, markerSourceId]);
    const allMapData = useMemo(() => history
        .map((historyItem, index) => {
            const markerData = extractMarkerDataFromPayload(
                historyItem?.payload?.[0] || null,
                useAlsMarker,
                buildMarkerLabel(markerSourceId, index),
                showGpsMarkers
            );
            if (!markerData) {
                return null;
            }

            return {
                ...markerData,
                markerId: `history-marker-${index}`
            };
        })
        .filter(Boolean), [history, markerSourceId, showGpsMarkers, useAlsMarker]);
    const allGpsMapData = useMemo(() => history
        .map((historyItem, index) => extractDeviceGpsMarkerDataFromPayload(
            historyItem?.payload?.[0] || null,
            buildMarkerLabel(markerSourceId, index),
            `gps-marker-${index}`
        ))
        .filter(Boolean), [history, markerSourceId]);
    const locationSummary = useMemo(() => extractLocationSummary(currentPayload, debugMode), [currentPayload, debugMode]);

    useEffect(() => {
        if (!call || !currentPayload) {
            onMarkerWarningChange("");
            return;
        }

        onMarkerWarningChange(currentMapData ? "" : "Marker cannot be displayed");
    }, [call, currentMapData, currentPayload, onMarkerWarningChange]);

    if (!call) {
        return <section className="flex h-full min-h-[32rem] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-1 items-center justify-center px-6 text-center text-slate-500">
                Select a call to view the map.
            </div>
        </section>;
    }

    return <section className="flex h-full min-h-[32rem] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
            <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Current Location</div>
                <div className="mt-2 space-y-2">
                    {locationSummary.primary && <div className="text-lg font-semibold text-slate-700">{locationSummary.primary}</div>}
                    {hasDisplayValue(locationSummary.floor) && <div className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800">
                        Floor: {locationSummary.floor}
                    </div>}
                    {locationSummary.room && <div className="inline-flex ml-2 rounded-full bg-sky-100 px-3 py-1 text-sm font-medium text-sky-800">
                        Room: {locationSummary.room}
                    </div>}
                    {locationSummary.otherLines.length > 0 && <div className="text-sm text-slate-500">
                        {locationSummary.otherLines.join(" | ")}
                    </div>}
                </div>
            </div>
        </div>
        <div className="min-h-0 flex-1">
            <EmbeddedEliMap
                initMapData={initMapData}
                mapData={showAllMapMarkers && showGpsMarkers ? null : (showAllMapMarkers ? null : currentMapData)}
                mapMarkers={showAllMapMarkers && !showGpsMarkers ? allMapData : []}
                gpsMapMarkers={showGpsMarkers
                    ? (showAllMapMarkers ? allGpsMapData : [currentGpsMapData].filter(Boolean))
                    : []}
                mapInitKey={call.callUuid}
            />
        </div>
    </section>;
};

export default Maps;

function extractInitMapDataFromPayload(payload) {
    const rankedLocation = payload?.rankedLocations?.[0];
    const eliCustomerId = rankedLocation?.tenantPublicId;

    if (!eliCustomerId) {
        return null;
    }

    return {
        eliCustomerId
    };
}

function extractMarkerDataFromPayload(payload, useAlsMarker = true, label = "", useGoogleMarkerIcon = false) {
    const rankedLocation = payload?.rankedLocations?.[0];
    const dispatchableAddress = rankedLocation?.dispatchableAddress;
    const useGpsCoordinates = useAlsMarker && payload?.gpsEstimated;
    const latitude = parseCoordinate(useGpsCoordinates ? payload?.gpsLatitude : rankedLocation?.tagGpsLatitude);
    const longitude = parseCoordinate(useGpsCoordinates ? payload?.gpsLongitude : rankedLocation?.tagGpsLongitude);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
    }

    return {
        floor: normalizeFloor(dispatchableAddress?.floor),
        icon: useGoogleMarkerIcon ? GOOGLE_MARKER_ICON : undefined,
        label,
        latitude,
        longitude
    };
}

function buildMarkerLabel(sourceIdentifier1 = "", logIndex = null) {
    if (logIndex === null || logIndex === undefined) {
        return sourceIdentifier1;
    }

    return `${sourceIdentifier1}${logIndex}`;
}

function extractDeviceGpsMarkerDataFromPayload(payload, label = "", markerId = "") {
    const rankedLocation = payload?.rankedLocations?.[0];
    const dispatchableAddress = rankedLocation?.dispatchableAddress;
    const latitude = parseCoordinate(payload?.gpsEstimated ? payload?.gpsReportedLatitude : payload?.gpsLatitude);
    const longitude = parseCoordinate(payload?.gpsEstimated ? payload?.gpsReportedLongitude : payload?.gpsLongitude);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
    }

    return {
        floor: normalizeFloor(dispatchableAddress?.floor),
        label,
        latitude,
        longitude,
        markerId
    };
}

function extractLocationSummary(payload, debugMode = false) {
    const rankedLocation = payload?.rankedLocations?.[0];
    const dispatchableAddress = rankedLocation?.dispatchableAddress;
    const hrl = rankedLocation?.humanReadableLocation;
    const parsedData = extractIndoorMapLink(hrl?.replace(/(\n)+/g, "\n") || "").parsedData;
    const visibleLines = debugMode
        ? parsedData.otherLines
        : parsedData.otherLines.filter((line) => !isDebugOnlyLine(line));

    return {
        floor: normalizeFloor(dispatchableAddress?.floor ?? parsedData.floor),
        otherLines: visibleLines,
        primary: dispatchableAddress?.installationDescription
            || dispatchableAddress?.businessName
            || parsedData.room
            || visibleLines[0]
            || "Resolved Location",
        room: parsedData.room
    };
}

function isDebugOnlyLine(line = "") {
    return line.startsWith("AP Lat/Lng:")
        || line.startsWith("AP Id:");
}

function normalizeFloor(floor) {
    if (floor === null || floor === undefined || floor === "") {
        return undefined;
    }

    const parsedFloor = Number(floor);
    return Number.isFinite(parsedFloor) ? parsedFloor : floor;
}

function parseCoordinate(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
}

function hasDisplayValue(value) {
    return value !== null && value !== undefined && value !== "";
}
